import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken, getUserId } from "../services/authorize";

const toDateInputValue = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const statusVariant = {
  available: "success",
  processing: "secondary",
  on_loan: "warning",
  reserved: "info",
  repair: "danger",
  lost: "dark",
  withdrawn: "secondary"
};

const statusLabel = {
  available: "พร้อมให้ยืม",
  processing: "กำลังดำเนินการ",
  on_loan: "กำลังถูกยืม",
  reserved: "ถูกจอง",
  repair: "ซ่อมแซม",
  lost: "สูญหาย",
  withdrawn: "ถอนออกจากระบบ"
};

export default function LibraryAcquisition() {
  const copyCodeInputRef = useRef(null);

  const [masterData, setMasterData] = useState({ categories: [], locations: [] });
  const [titles, setTitles] = useState([]);
  const [copies, setCopies] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);

  const [yearEra, setYearEra] = useState('CE');
  const [submittingTitle, setSubmittingTitle] = useState(false);
  const [submittingCopy, setSubmittingCopy] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [titleForm, setTitleForm] = useState({
    title_code: "",
    isbn: "",
    title_name: "",
    author_name: "",
    edition_no: "",
    call_number: "",
    publish_year: "",
    category_id: "",
    book_cover: ""
  });

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);

  const [copyForm, setCopyForm] = useState({
    copy_code: "",
    title_id: "",
    location_id: "",
    shelf_code: "",
    acq_price: "",
    acq_date: toDateInputValue(new Date()),
    is_reference: false,
    copy_note: ""
  });

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [masterRes, titlesRes, copiesRes, shelvesRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getMasterData`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getTitles`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getCopies`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getShelves`, { headers })
      ]);

      const nextMaster = masterRes.data || { categories: [], locations: [] };
      const nextTitles = titlesRes.data || [];
      const nextCopies = copiesRes.data || [];
      const nextShelves = shelvesRes.data || [];

      setMasterData(nextMaster);
      setTitles(nextTitles);
      setCopies(nextCopies);
      setShelves(nextShelves);

      setCopyForm((prev) => ({
        ...prev,
        title_id: prev.title_id || String(nextTitles[0]?.title_id || "")
      }));
    } catch (fetchError) {
      setError("ไม่สามารถโหลดข้อมูลนำเข้าหนังสือได้ กรุณาลองใหม่อีกครั้ง");
      console.error("LibraryAcquisition fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!loading) {
      copyCodeInputRef.current?.focus();
    }
  }, [loading]);

  const selectedTitle = useMemo(() => {
    if (!copyForm.title_id) return null;
    return titles.find((title) => Number(title.title_id) === Number(copyForm.title_id)) || null;
  }, [titles, copyForm.title_id]);

  const titleOptions = useMemo(
    () =>
      titles.map((title) => ({
        value: String(title.title_id),
        label: `${title.title_code} - ${title.title_name}`
      })),
    [titles]
  );

  const selectedCopyTitleOption = useMemo(
    () => titleOptions.find((option) => option.value === String(copyForm.title_id)) || null,
    [titleOptions, copyForm.title_id]
  );

  const filteredShelfOptions = useMemo(() => {
    if (!copyForm.location_id) return [];

    return shelves
      .filter(
        (shelf) =>
          Number(shelf.tb_library_location_loc_id) === Number(copyForm.location_id) &&
          shelf.shelf_status === "active"
      )
      .map((shelf) => ({
        value: shelf.shelf_code,
        label: `${shelf.shelf_code}${shelf.shelf_name ? ` - ${shelf.shelf_name}` : ""}`
      }));
  }, [shelves, copyForm.location_id]);

  useEffect(() => {
    if (!copyForm.location_id && copyForm.shelf_code) {
      setCopyForm((prev) => ({ ...prev, shelf_code: "" }));
      return;
    }

    if (!copyForm.shelf_code) return;
    const stillValid = filteredShelfOptions.some((option) => option.value === copyForm.shelf_code);
    if (!stillValid) {
      setCopyForm((prev) => ({ ...prev, shelf_code: "" }));
    }
  }, [copyForm.location_id, copyForm.shelf_code, filteredShelfOptions]);

  const handleTitleSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();
    setError("");
    setSuccess("");

    const officerId = getUserId();
    if (!officerId) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!titleForm.title_code.trim() || !titleForm.title_name.trim() || !titleForm.category_id) {
      setError("กรุณากรอกข้อมูลชื่อหนังสือให้ครบ");
      return;
    }

    try {
      setSubmittingTitle(true);
      let coverPath = titleForm.book_cover || null;
      if (coverFile) {
        try {
          coverPath = await uploadCoverFile();
        } catch (uploadError) {
          setError("อัพโหลดรูปภาพหน้าปกไม่สำเร็จ");
          return;
        }
      }

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/addTitle`,
        {
          title_code: titleForm.title_code.trim(),
          isbn: titleForm.isbn.trim() || null,
          title_name: titleForm.title_name.trim(),
          author_name: titleForm.author_name.trim() || null,
          edition_no: titleForm.edition_no.trim() || null,
          call_number: titleForm.call_number.trim() || null,
          publish_year: titleForm.publish_year || null,
          book_cover: coverPath || null,
          category_id: Number(titleForm.category_id),
          t_id: officerId
        },
        { headers }
      );

      const createdTitleId = response.data?.title_id;
      setSuccess(`เพิ่มชื่อหนังสือสำเร็จ (ID: ${createdTitleId || "-"})`);
      setTitleForm({
        title_code: "",
        isbn: "",
        title_name: "",
        author_name: "",
        edition_no: "",
        call_number: "",
        publish_year: "",
        category_id: "",
        book_cover: ""
      });
      setCoverFile(null);
      setCoverPreview("");

      await fetchData();

      if (createdTitleId) {
        setCopyForm((prev) => ({ ...prev, title_id: String(createdTitleId) }));
      }
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "duplicate title code") {
        setError("รหัสชื่อหนังสือซ้ำในระบบ");
      } else if (message === "missing required title fields") {
        setError("ข้อมูลชื่อหนังสือไม่ครบถ้วน");
      } else {
        setError("เพิ่มชื่อหนังสือไม่สำเร็จ");
      }
      console.error("Title submit error:", submitError);
    } finally {
      setSubmittingTitle(false);
      setLoading(false);
    }
  };

  const handleCoverFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setCoverPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadCoverFile = async () => {
    if (!coverFile) return titleForm.book_cover || null;

    setUploadingCover(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append('coverImage', coverFile);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/uploadCover`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const coverPath = response.data.coverPath;
      setTitleForm((prev) => ({ ...prev, book_cover: coverPath }));
      return coverPath;
    } catch (uploadError) {
      console.error("Cover upload error:", uploadError);
      throw uploadError;
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveCover = () => {
    setCoverFile(null);
    setCoverPreview("");
    setTitleForm((prev) => ({ ...prev, book_cover: "" }));
  };

  const handleCopySubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const officerId = getUserId();
    if (!officerId) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!copyForm.copy_code.trim() || !copyForm.title_id) {
      setError("กรุณากรอกรหัสเล่มและเลือกชื่อหนังสือ");
      return;
    }

    try {
      setSubmittingCopy(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/addCopy`,
        {
          copy_code: copyForm.copy_code.trim(),
          title_id: Number(copyForm.title_id),
          location_id: copyForm.location_id ? Number(copyForm.location_id) : null,
          shelf_code: copyForm.shelf_code.trim() || null,
          acq_price: copyForm.acq_price ? Number(copyForm.acq_price) : 0,
          acq_date: copyForm.acq_date || null,
          is_reference: copyForm.is_reference,
          copy_note: copyForm.copy_note.trim() || null,
          t_id: officerId
        },
        { headers }
      );

      setSuccess(`เพิ่มเล่มสำเร็จ รหัสเล่ม: ${response.data?.copy_code || "-"}`);
      setCopyForm((prev) => ({
        ...prev,
        copy_code: "",
        shelf_code: "",
        acq_price: "",
        copy_note: "",
        is_reference: false,
        acq_date: toDateInputValue(new Date())
      }));

      await fetchData();
      copyCodeInputRef.current?.focus();
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "duplicate copy code") {
        setError("รหัสเล่มซ้ำในระบบ");
      } else if (message === "title not found") {
        setError("ไม่พบชื่อหนังสือที่เลือก");
      } else if (message === "location not found") {
        setError("ไม่พบตำแหน่งจัดเก็บที่เลือก");
      } else if (message === "missing required copy fields") {
        setError("ข้อมูลเล่มหนังสือไม่ครบถ้วน");
      } else {
        setError("เพิ่มเล่มหนังสือไม่สำเร็จ");
      }
      console.error("Copy submit error:", submitError);
    } finally {
      setSubmittingCopy(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">นำเข้าหนังสือ</h4>
          <Badge bg="primary">Acquisition</Badge>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col lg={5}>
            <Card>
              <Card.Header className="fw-semibold">เพิ่มชื่อหนังสือใหม่</Card.Header>
              <Card.Body>
                <Form onSubmit={handleTitleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>รหัสชื่อหนังสือ</Form.Label>
                    <Form.Control
                      value={titleForm.title_code}
                      onChange={(event) => setTitleForm((prev) => ({ ...prev, title_code: event.target.value }))}
                      placeholder="เช่น LIB-TTL-0100"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>ชื่อหนังสือ</Form.Label>
                    <Form.Control
                      value={titleForm.title_name}
                      onChange={(event) => setTitleForm((prev) => ({ ...prev, title_name: event.target.value }))}
                      required
                    />
                  </Form.Group>

                  <Row className="g-2">
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>ชื่อผู้เขียน</Form.Label>
                        <Form.Control
                          value={titleForm.author_name}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, author_name: event.target.value }))}
                          placeholder="เช่น สมชาย ใจดี"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>ครั้งที่พิมพ์</Form.Label>
                        <Form.Control
                          value={titleForm.edition_no}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, edition_no: event.target.value }))}
                          placeholder="เช่น พิมพ์ครั้งที่ 3"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>หมวดหมู่</Form.Label>
                    <Form.Select
                      value={titleForm.category_id}
                      onChange={(event) => setTitleForm((prev) => ({ ...prev, category_id: event.target.value }))}
                      required
                    >
                      <option value="">เลือกหมวดหมู่</option>
                      {(masterData.categories || []).map((category) => (
                        <option key={category.category_id} value={category.category_id}>
                          {category.category_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ISBN</Form.Label>
                        <Form.Control
                          value={titleForm.isbn}
                          onChange={(event) => setTitleForm((prev) => ({ ...prev, isbn: event.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <Form.Label className="mb-0">ปีพิมพ์</Form.Label>
                          <ButtonGroup size="sm">
                            <Button
                              variant={yearEra === 'CE' ? 'primary' : 'outline-primary'}
                              onClick={() => setYearEra('CE')}
                            >ค.ศ.</Button>
                            <Button
                              variant={yearEra === 'BE' ? 'primary' : 'outline-primary'}
                              onClick={() => setYearEra('BE')}
                            >พ.ศ.</Button>
                          </ButtonGroup>
                        </div>
                        <Form.Control
                          type="number"
                          min={yearEra === 'BE' ? 2443 : 1900}
                          max={yearEra === 'BE' ? 2643 : 2100}
                          placeholder={yearEra === 'BE' ? 'เช่น 2567' : 'เช่น 2024'}
                          value={
                            titleForm.publish_year === '' ? '' :
                            yearEra === 'BE' ? Number(titleForm.publish_year) + 543 : titleForm.publish_year
                          }
                          onChange={(event) => {
                            const raw = event.target.value;
                            const stored = raw === '' ? '' : yearEra === 'BE' ? Number(raw) - 543 : raw;
                            setTitleForm((prev) => ({ ...prev, publish_year: stored }));
                          }}
                        />
                        <Form.Text className="text-muted">
                          {titleForm.publish_year !== '' && (
                            yearEra === 'BE'
                              ? `ค.ศ. ${titleForm.publish_year}`
                              : `พ.ศ. ${Number(titleForm.publish_year) + 543}`
                          )}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>เลขหมู่หนังสือ</Form.Label>
                    <Form.Control
                      value={titleForm.call_number}
                      onChange={(event) => setTitleForm((prev) => ({ ...prev, call_number: event.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>รูปภาพหน้าปก</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleCoverFileChange}
                      disabled={uploadingCover}
                    />
                    {coverPreview && (
                      <div className="mt-2">
                        <img
                          src={coverPreview}
                          alt="Cover preview"
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                          className="border"
                        />
                      </div>
                    )}
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveCover}
                        disabled={!coverFile && !titleForm.book_cover}
                      >
                        ลบรูปภาพ
                      </Button>
                    </div>
                  </Form.Group>

                  <Button type="submit" disabled={submittingTitle}>
                    {submittingTitle ? "กำลังบันทึก..." : "เพิ่มชื่อหนังสือ"}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card>
              <Card.Header className="fw-semibold">นำเข้าเล่มหนังสือ</Card.Header>
              <Card.Body>
                <Form onSubmit={handleCopySubmit}>
                  <Row className="g-2">
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>รหัสเล่ม</Form.Label>
                        <Form.Control
                          ref={copyCodeInputRef}
                          value={copyForm.copy_code}
                          onChange={(event) => setCopyForm((prev) => ({ ...prev, copy_code: event.target.value }))}
                          placeholder="เช่น BK-000123"
                          required
                        />
                        {/* <Form.Text className="text-muted">สแกนจากเครื่องยิงได้ทันที</Form.Text> */}
                      </Form.Group>
                    </Col>
                    <Col md={7}>
                      <Form.Group className="mb-3">
                        <Form.Label>ชื่อหนังสือ</Form.Label>
                        <Select
                          options={titleOptions}
                          value={selectedCopyTitleOption}
                          onChange={(option) => setCopyForm((prev) => ({ ...prev, title_id: option?.value || "" }))}
                          isSearchable
                          isClearable
                          placeholder="เลือกชื่อหนังสือ"
                          noOptionsMessage={() => "ไม่พบข้อมูล"}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {selectedTitle && (
                    <Alert variant="light" className="mb-3 border">
                      เลือกแล้ว: {selectedTitle.title_name} (พร้อมยืม {Number(selectedTitle.available_count || 0)} / ทั้งหมด {Number(selectedTitle.copy_count || 0)})
                    </Alert>
                  )}

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ตำแหน่งจัดเก็บ</Form.Label>
                        <Form.Select
                          value={copyForm.location_id}
                          onChange={(event) =>
                            setCopyForm((prev) => ({
                              ...prev,
                              location_id: event.target.value,
                              shelf_code: ""
                            }))
                          }
                        >
                          <option value="">ไม่ระบุ</option>
                          {(masterData.locations || []).map((location) => (
                            <option key={location.loc_id} value={location.loc_id}>
                              {location.loc_code} - {location.loc_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>รหัสชั้นวาง</Form.Label>
                        <Form.Select
                          value={copyForm.shelf_code}
                          onChange={(event) => setCopyForm((prev) => ({ ...prev, shelf_code: event.target.value }))}
                          disabled={!copyForm.location_id}
                        >
                          <option value="">
                            {!copyForm.location_id ? "เลือกตำแหน่งจัดเก็บก่อน" : "เลือกรหัสชั้นวาง"}
                          </option>
                          {filteredShelfOptions.map((shelfOption) => (
                            <option key={shelfOption.value} value={shelfOption.value}>
                              {shelfOption.label}
                            </option>
                          ))}
                        </Form.Select>
                        {copyForm.location_id && filteredShelfOptions.length === 0 && (
                          <Form.Text className="text-muted">
                            ยังไม่มีรหัสชั้นวางในตำแหน่งนี้ กรุณาเพิ่มในหน้าจัดการข้อมูลแม่บท
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ราคาได้มา</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          step="0.01"
                          value={copyForm.acq_price}
                          onChange={(event) => setCopyForm((prev) => ({ ...prev, acq_price: event.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>วันที่รับเข้า</Form.Label>
                        <Form.Control
                          type="date"
                          value={copyForm.acq_date}
                          onChange={(event) => setCopyForm((prev) => ({ ...prev, acq_date: event.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="เป็นหนังสืออ้างอิง (Reference)"
                      checked={copyForm.is_reference}
                      onChange={(event) => setCopyForm((prev) => ({ ...prev, is_reference: event.target.checked }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>หมายเหตุ</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={copyForm.copy_note}
                      onChange={(event) => setCopyForm((prev) => ({ ...prev, copy_note: event.target.value }))}
                    />
                  </Form.Group>

                  <Button variant="success" type="submit" disabled={submittingCopy}>
                    {submittingCopy ? "กำลังบันทึก..." : "เพิ่มเล่มหนังสือ"}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
            <span>รายการเล่มที่นำเข้าล่าสุด</span>
            {loading && <Spinner animation="border" size="sm" />}
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>รหัสเล่ม</th>
                  <th>ชื่อหนังสือ</th>
                  <th>ผู้เขียน</th>
                  <th>ครั้งที่พิมพ์</th>
                  <th>ตำแหน่ง</th>
                  <th>ชั้นวาง</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {!loading && copies.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-3">
                      ยังไม่มีข้อมูลเล่มหนังสือ
                    </td>
                  </tr>
                )}
                {copies.slice(0, 30).map((copy) => (
                  <tr key={copy.copy_id}>
                    <td>{copy.copy_code}</td>
                    <td>{copy.title_name}</td>
                    <td>{copy.author_name || "-"}</td>
                    <td>{copy.edition_no || "-"}</td>
                    <td>{copy.loc_name || "-"}</td>
                    <td>{copy.shelf_code || "-"}</td>
                    <td>
                      <Badge bg={statusVariant[copy.copy_status] || "secondary"}>
                        {statusLabel[copy.copy_status] || copy.copy_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </main>
      <Footer />
    </React.Fragment>
  );
}
